import {ThemeConfig} from "antd";

// Single source of the ATI palette. CSS reads the same values as `--ati-*` variables
// (see applyAtiCssVariables); never repeat these hex values in components or stylesheets.
export const atiColors = {
    blue: "#223F6B",
    blueDark: "#162B4A",
    blueSoft: "#EDF3FB",
    blueLine: "rgba(34, 63, 107, 0.22)",
    orange: "#EE7436",
    orangeSoft: "#FFF2EB",
    orangeLine: "rgba(238, 116, 54, 0.24)",
    // Orange and green as text: the brand hues miss 4.5:1 on white and on their soft tints.
    orangeText: "#B8501A",
    yellow: "#F7CC32",
    yellowSoft: "#FFF8DB",
    green: "#11845B",
    greenSoft: "#EAF8F0",
    greenLine: "rgba(17, 132, 91, 0.22)",
    greenText: "#0E6B49",
    red: "#BF2A2A",
    redSoft: "#FFF0F0",
    redLine: "rgba(191, 42, 42, 0.22)",
    ink: "#182236",
    muted: "#68758A",
    line: "#DCE3EF",
    soft: "#F5F7FB",
    white: "#FFFFFF",
    tableHeader: "#F8FBFF",
    rowHover: "rgba(34, 63, 107, 0.035)",
    rowSelected: "#F4F7FB"
};

export const atiShadows = {
    panel: "0 14px 38px rgba(34, 63, 107, 0.10)",
    button: "0 8px 18px rgba(34, 63, 107, 0.16)"
};

export const atiFontFamily = "\"Merriweather Sans\", Arial, sans-serif";

const toHsl = (hex: string): [number, number, number] => {
    const [r, g, b] = [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;
    const delta = max - min;
    if (delta === 0) return [0, 0, lightness];
    const saturation = delta / (1 - Math.abs(2 * lightness - 1));
    const hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
    return [(hue * 60 + 360) % 360, saturation, lightness];
};

const fromHsl = (hue: number, saturation: number, lightness: number): string => {
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const channel = (offset: number) => {
        const k = (offset + hue / 30) % 12;
        return lightness - chroma / 2 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    };
    return `#${[0, 8, 4].map(offset => Math.round(channel(offset) * 255).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
};

// Light tint keeping the colour's hue, at the saturation and lightness of ATI blue-soft.
// antd's generated palette turns dark, muted colours such as ATI blue into grey (#A1A6AB),
// so selected/hover fills must not come from colorPrimary derivation.
export const softTint = (hex: string, lightness = 0.957): string => {
    const [hue, saturation] = toHsl(hex);
    return fromHsl(hue, saturation < 0.1 ? saturation : 0.63, lightness);
};

// Selected and hover fills for options, menu items, date cells and selectable cards.
export const selectionTokens = (primary: string, soft = softTint(primary)) => ({
    colorPrimaryBg: soft,
    colorPrimaryBgHover: softTint(primary, 0.92),
    colorPrimaryBorder: softTint(primary, 0.8),
    controlItemBgHover: soft,
    controlItemBgActive: soft,
    controlItemBgActiveHover: softTint(primary, 0.92)
});

const toKebabCase = (value: string) => value.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);

export const atiCssVariables: Record<string, string> = {
    ...Object.fromEntries(Object.entries(atiColors).map(([name, value]) => [`--ati-${toKebabCase(name)}`, value])),
    "--ati-shadow": atiShadows.panel,
    "--ati-font-family": atiFontFamily
};

export const applyAtiCssVariables = (target: HTMLElement = document.documentElement) => {
    Object.entries(atiCssVariables).forEach(([name, value]) => target.style.setProperty(name, value));
};

export const atiTheme: ThemeConfig = {
    token: {
        colorPrimary: atiColors.blue,
        colorInfo: atiColors.blue,
        colorSuccess: atiColors.green,
        colorWarning: atiColors.orange,
        colorError: atiColors.red,
        colorText: atiColors.ink,
        colorTextSecondary: atiColors.muted,
        colorBorder: atiColors.line,
        colorBorderSecondary: atiColors.line,
        colorBgLayout: atiColors.soft,
        colorBgContainer: atiColors.white,
        colorBgElevated: atiColors.white,
        ...selectionTokens(atiColors.blue),
        colorFillAlter: atiColors.blueSoft,
        colorFillSecondary: atiColors.soft,
        colorInfoBg: atiColors.blueSoft,
        colorInfoBorder: atiColors.blueLine,
        colorSuccessBg: atiColors.greenSoft,
        colorSuccessBorder: atiColors.greenLine,
        colorSuccessText: atiColors.greenText,
        colorWarningBg: atiColors.orangeSoft,
        colorWarningBorder: atiColors.orangeLine,
        colorWarningText: atiColors.orangeText,
        colorErrorBg: atiColors.redSoft,
        colorErrorBorder: atiColors.redLine,
        fontFamily: atiFontFamily,
        borderRadius: 8,
        borderRadiusLG: 8,
        boxShadow: atiShadows.panel,
        boxShadowSecondary: atiShadows.panel,
        controlHeight: 38,
        controlHeightLG: 44
    },
    components: {
        Button: {
            fontWeight: 700,
            primaryShadow: atiShadows.button
        },
        Card: {
            headerBg: atiColors.white
        },
        Form: {
            itemMarginBottom: 14,
            labelColor: atiColors.muted,
            marginLG: 12
        },
        Layout: {
            bodyBg: atiColors.soft,
            headerBg: atiColors.white,
            siderBg: atiColors.blueDark
        },
        Menu: {
            darkItemBg: atiColors.blueDark,
            darkSubMenuItemBg: atiColors.blueDark,
            darkItemSelectedBg: atiColors.blue,
            darkItemHoverBg: "rgba(255, 255, 255, 0.10)",
            itemBorderRadius: 8,
            fontSizeIcon: 22
        },
        Select: {
            optionSelectedColor: atiColors.blue
        },
        Segmented: {
            itemSelectedBg: atiColors.white,
            itemSelectedColor: atiColors.blue,
            trackBg: atiColors.blueSoft
        },
        Table: {
            borderColor: atiColors.line,
            headerBg: atiColors.tableHeader,
            headerColor: atiColors.blue,
            rowHoverBg: atiColors.rowHover,
            rowSelectedBg: atiColors.rowSelected,
            rowSelectedHoverBg: atiColors.blueSoft
        },
        Tag: {
            borderRadiusSM: 999
        },
        Tabs: {
            itemActiveColor: atiColors.blue,
            itemSelectedColor: atiColors.blue,
            inkBarColor: atiColors.orange
        }
    }
};
